import { IonContent, IonPage } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useHistory } from "react-router-dom";

import { I } from "./icons";
import { Ring } from "./Ring";
import "./PlayerShell.css";

/**
 * The one player (audit §5 "Player family"): workout, treadmill and walk all draw through this, so
 * they cannot drift back into three different apps. It owns layout only — every clock, ping and
 * store write stays in the page that owns the session.
 *
 * Exactly one `IonPage`, whatever the stage: the finished screen is a prop, not a second
 * component, because a different root component would remount the page mid-route.
 */

export type PlayerSurface = "ink" | "work" | "rest" | "done";

export interface PlayerStat {
  figure: string | number | null;
  label: string;
  /** "~" before the figure and ", est." after the label */
  estimated?: boolean;
  /** provenance, e.g. `<Tag tone="phone">phone · live</Tag>` */
  tag?: ReactNode;
}

export interface PlayerAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  /** when the visible label is not the whole story */
  ariaLabel?: string;
}

export interface PlayerMenuItem {
  label: string;
  onClick: () => void;
  /** set for a toggle: rendered as a checkbox item with its state in words */
  checked?: boolean;
}

export interface PlayerEnd {
  /** the menu item and the confirm button: "End walk" */
  label: string;
  /** one sentence saying what ending does */
  question: string;
  onConfirm: () => void;
}

export interface PlayerFinished {
  /** "Treadmill done" */
  heading: string;
  sub?: ReactNode;
  stats: PlayerStat[];
  line?: ReactNode;
  quote?: string;
  onDone: () => void;
  secondary?: PlayerAction | null;
  /** the Done button's word, in the player's language (Simple) */
  doneLabel?: string;
}

/**
 * The Simple player (ADR-055): the same shell under `k-player--simple`, with the bigger tokens,
 * the name and cue above the ring beside the move's drawing, "More" in words, no elapsed clock and
 * no segment bar. Its words come from `domain/playerView.ts`, in the player's language.
 */
export interface PlayerSimple {
  /** "More" in the player's language */
  more: string;
  /** a toggle's state in the menu */
  on: string;
  off: string;
  /** the other language's name, muted under the name */
  sub?: ReactNode;
  /** the move's drawing (`MoveFigure`), beside the name and cue */
  figure?: ReactNode;
  /** the rest's one word at the large size */
  large?: boolean;
  /** the page's language, and the parts that differ from it */
  lang?: string;
  nameLang?: string;
  subLang?: string;
  cueLang?: string;
}

export interface PlayerShellProps {
  surface: PlayerSurface;
  title: string;
  /** Minimise on a live player; Close on the finished screen */
  onMinimise: () => void;
  elapsed?: string | null;
  info?: PlayerAction | null;
  menu?: PlayerMenuItem[];
  end?: PlayerEnd | null;
  /** a `SegmentBar onSurface` */
  segments?: ReactNode;
  /** the words for the segment bar: "Phase 3 of 7 · 13:24 to go" */
  line?: ReactNode;
  ring?: {
    pct: number;
    figure: ReactNode;
    unit?: ReactNode;
    /** the whole ring in words, for a screen reader */
    label: string;
    glyph?: ReactNode;
    motion?: "pulse" | "breathe" | null;
  } | null;
  /** directly under the ring: the rest's ±15 s */
  adjust?: ReactNode;
  name?: ReactNode;
  cue?: ReactNode;
  /** the treadmill's speed and incline */
  pair?: { figure: string; unit: string }[];
  next?: ReactNode;
  stats?: PlayerStat[];
  note?: ReactNode;
  left?: PlayerAction | null;
  /** the 64px round centre control: Pause / Resume, or Redo once a move's time is up */
  centre?: { label: string; icon: ReactNode; onClick: () => void } | null;
  right?: PlayerAction | null;
  finished?: PlayerFinished | null;
  /** overlays: the exercise sheet */
  children?: ReactNode;
  simple?: PlayerSimple | null;
}

/** The previous-move glyph, filled like play/pause/skip. */
export const prevIcon: ReactElement = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19 5L9 12l10 7zM7 5H5v14h2z" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Where Minimise, Close and End go: the page the player was started from.
 *
 * A starter may say so in the route state (`history.push("/walk", { from: "/today" })`); it is
 * read once, at mount, from the entry that opened the player. A resume from the LiveBar or a
 * relaunch carries no state, and then the session's kind decides — which is what the audit means
 * by "remembered in the session record": the record already names the kind (and, for a workout,
 * rescue/extra), so no new persisted field is needed.
 */
export function usePlayerOrigin(fallback: string): string {
  const history = useHistory();
  const [origin] = useState(() => {
    const st: unknown = history.location.state;
    if (st && typeof st === "object" && "from" in st) {
      const from = (st as { from: unknown }).from;
      if (typeof from === "string" && from.startsWith("/") && !isPlayerRoute(from)) return from;
    }
    return fallback;
  });
  return origin;
}

function isPlayerRoute(p: string): boolean {
  const path = p.split("?")[0];
  return path === "/workout" || path === "/treadrun" || path === "/walk";
}

function statFigure(s: PlayerStat): string {
  if (s.figure === null || s.figure === "") return "–";
  const f = typeof s.figure === "number" ? s.figure.toLocaleString() : s.figure;
  return s.estimated ? `~${f}` : f;
}

function Stats({ stats }: { stats: PlayerStat[] }) {
  return (
    <div className={`k-player__stats${stats.length === 4 ? " k-player__stats--4" : ""}`}>
      {stats.map((s) => (
        <div className="k-player__stat" key={s.label}>
          <span className="k-player__statnum">{statFigure(s)}</span>
          <span className="k-player__statlabel">
            {s.label}
            {s.estimated && s.figure !== null ? ", est." : ""}
            {s.tag}
          </span>
        </div>
      ))}
    </div>
  );
}

function SideButton({ action, primary }: { action: PlayerAction; primary?: boolean }) {
  return (
    <button
      type="button"
      className={`k-player__btn${primary ? " k-player__btn--primary" : ""}`}
      onClick={action.onClick}
      aria-label={action.ariaLabel}
    >
      {action.icon}
      <span>{action.label}</span>
    </button>
  );
}

/** More: the toggles and End. End opens an inline confirm rather than acting on one tap. */
function MoreMenu({ items, end, words }: { items: PlayerMenuItem[]; end: PlayerEnd | null; words?: PlayerSimple | null }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);

  const close = () => {
    setOpen(false);
    setConfirming(false);
    btn.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const what = [end ? end.label.toLowerCase() : null, items.length ? "settings" : null]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="k-player__more">
      <button
        ref={btn}
        type="button"
        className={words ? "k-player__moretext" : "k-iconbtn k-iconbtn--on-surface"}
        aria-label={words ? undefined : what ? `More: ${what}` : "More"}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {words ? words.more : I.more}
      </button>
      {open ? (
        <>
          <button type="button" className="k-player__scrim" aria-label="Close menu" onClick={close} />
          <div className="k-player__menu" role={confirming ? "alertdialog" : "menu"}>
            {confirming && end ? (
              <div className="k-player__confirm">
                <p>{end.question}</p>
                <div className="k-player__confirmrow">
                  <button type="button" className="k-btn k-btn--ghost k-btn--grow" onClick={close}>
                    Keep going
                  </button>
                  <button
                    type="button"
                    className="k-btn k-btn--danger k-btn--grow"
                    onClick={() => {
                      setOpen(false);
                      setConfirming(false);
                      end.onConfirm();
                    }}
                  >
                    {end.label}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {items.map((it) => (
                  <button
                    key={it.label}
                    type="button"
                    className="k-player__item"
                    role={it.checked === undefined ? "menuitem" : "menuitemcheckbox"}
                    aria-checked={it.checked}
                    onClick={() => {
                      close();
                      it.onClick();
                    }}
                  >
                    <span>{it.label}</span>
                    {it.checked === undefined ? null : (
                      <span className="k-player__itemval">{it.checked ? words?.on ?? "On" : words?.off ?? "Off"}</span>
                    )}
                  </button>
                ))}
                {end ? (
                  <button
                    type="button"
                    className="k-player__item k-player__item--danger"
                    role="menuitem"
                    onClick={() => setConfirming(true)}
                  >
                    <span>{end.label}</span>
                  </button>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function PlayerShell(p: PlayerShellProps) {
  const surface: PlayerSurface = p.finished ? "done" : p.surface;
  const fin = p.finished;
  const hasMenu = !fin && ((p.menu && p.menu.length > 0) || !!p.end);
  const simple = p.simple ?? null;

  // Simple: the name, the other language's name and the cue first, beside the drawing, because
  // they are what the reader needs before the clock (board PlayerSimpleWork).
  const head = simple ? (
    <div className="k-player__head">
      <div className="k-player__headtext">
        {p.name ? (
          <h1 className={`k-player__name${simple.large ? " k-player__name--lg" : ""}`} lang={simple.nameLang}>
            {p.name}
          </h1>
        ) : null}
        {simple.sub ? (
          <p className="k-player__sub" lang={simple.subLang}>
            {simple.sub}
          </p>
        ) : null}
        {p.cue ? (
          <p className="k-player__cue" lang={simple.cueLang}>
            {p.cue}
          </p>
        ) : null}
      </div>
      {simple.figure ?? null}
    </div>
  ) : null;

  return (
    <IonPage className={`k-player-page k-player--${surface}${simple ? " k-player--simple" : ""}`}>
      <IonContent>
        <div className="screen full">
          <div className="inner">
            <div className="k-player" lang={simple?.lang}>
              <div className="k-player__top">
                <button
                  type="button"
                  className="k-iconbtn k-iconbtn--on-surface"
                  aria-label={fin ? "Close" : "Minimise"}
                  onClick={p.onMinimise}
                >
                  {fin ? I.close : I["chevron-down"]}
                </button>
                <p className="k-player__title">{p.title}</p>
                {!fin && p.elapsed && !simple ? (
                  <span className="k-player__elapsed" aria-label={`Elapsed ${p.elapsed}`}>
                    {p.elapsed}
                  </span>
                ) : null}
                {!fin && p.info ? (
                  <button
                    type="button"
                    className="k-iconbtn k-iconbtn--on-surface"
                    aria-label={p.info.ariaLabel ?? p.info.label}
                    onClick={p.info.onClick}
                  >
                    {I.info}
                  </button>
                ) : null}
                {hasMenu ? <MoreMenu items={p.menu ?? []} end={p.end ?? null} words={simple} /> : null}
              </div>

              {fin ? (
                <>
                  <div className="k-player__done">
                    <svg className="k-player__check" viewBox="0 0 128 128" role="img" aria-label="Finished">
                      <circle cx="64" cy="64" r="58" />
                      <path d="M38 66l18 18 34-38" />
                    </svg>
                    <div className="k-player__meta">
                      <h1 className="k-player__name">{fin.heading}</h1>
                      {fin.sub ? <p className="k-player__cue">{fin.sub}</p> : null}
                    </div>
                  </div>
                  <Stats stats={fin.stats} />
                  <div className="k-player__meta k-player__meta--after">
                    {fin.line ? <p className="k-player__line-strong">{fin.line}</p> : null}
                    {fin.quote ? <p className="k-player__quote">{fin.quote}</p> : null}
                  </div>
                  <div className="k-player__gap k-player__gap--40" />
                  <div className="k-player__finctl">
                    <button
                      type="button"
                      className="k-player__btn k-player__btn--primary k-player__btn--lg"
                      onClick={fin.onDone}
                    >
                      {I.tick}
                      <span>{fin.doneLabel ?? "Done"}</span>
                    </button>
                    {fin.secondary ? (
                      <button
                        type="button"
                        className="k-player__btn k-player__btn--lg"
                        onClick={fin.secondary.onClick}
                        aria-label={fin.secondary.ariaLabel}
                      >
                        {fin.secondary.icon}
                        <span>{fin.secondary.label}</span>
                      </button>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  {head}
                  {!simple && (p.segments || p.line) ? (
                    <div className="k-player__progress">
                      {p.segments}
                      {p.line ? <p className="k-player__line">{p.line}</p> : null}
                    </div>
                  ) : null}

                  <div className="k-player__figure">
                    {p.ring ? (
                      <div
                        className={`k-player__ring${p.ring.motion ? ` k-player__ring--${p.ring.motion}` : ""}`}
                        role="img"
                        aria-label={p.ring.label}
                      >
                        <Ring
                          pct={p.ring.pct}
                          size={248}
                          stroke={12}
                          color="var(--on-surface)"
                          track="var(--on-surface-track)"
                          style={{ width: "var(--k-player-ring)", height: "var(--k-player-ring)" }}
                        >
                          <div aria-hidden="true" className="k-player__ringc">
                            {p.ring.glyph ? <div className="k-player__glyph">{p.ring.glyph}</div> : null}
                            <div className="k-player__fig">{p.ring.figure}</div>
                            {p.ring.unit ? <div className="k-player__unit">{p.ring.unit}</div> : null}
                          </div>
                        </Ring>
                      </div>
                    ) : null}
                    {p.adjust ? <div className="k-player__adjust">{p.adjust}</div> : null}
                  </div>

                  {!simple && (p.name || p.cue) ? (
                    <div className="k-player__meta">
                      {p.name ? <p className="k-player__name">{p.name}</p> : null}
                      {p.cue ? <p className="k-player__cue">{p.cue}</p> : null}
                    </div>
                  ) : null}

                  {p.pair && p.pair.length ? (
                    <div className="k-player__pair">
                      {p.pair.map((x) => (
                        <div key={x.unit}>
                          <span className="k-player__pairnum">{x.figure}</span>
                          <span className="k-player__pairunit">{x.unit}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {p.next ? (
                    <div className="k-player__meta">
                      <p className="k-player__next">{p.next}</p>
                    </div>
                  ) : null}

                  <div className="k-player__gap" />
                  {p.stats && p.stats.length ? <Stats stats={p.stats} /> : null}
                  {p.note ? <p className="k-player__note">{p.note}</p> : null}
                  <div className="k-player__gap k-player__gap--40" />

                  <div className="k-player__ctl">
                    <div className="k-player__slot">
                      {p.left ? <SideButton action={p.left} /> : null}
                    </div>
                    <div className="k-player__slot k-player__slot--centre">
                      {p.centre ? (
                        <button
                          type="button"
                          className="k-player__round"
                          aria-label={p.centre.label}
                          onClick={p.centre.onClick}
                        >
                          {p.centre.icon}
                        </button>
                      ) : null}
                    </div>
                    <div className="k-player__slot">
                      {p.right ? <SideButton action={p.right} primary={!simple || !p.centre} /> : null}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {p.children}
      </IonContent>
    </IonPage>
  );
}
